/**
 * DevicePerfProfile — classify device performance tier from SCRFD pre-warm latency
 * and map it to a detection downscale factor.
 *
 * PR #4 — adaptive-frame-pacing
 *
 * IMPORTANT: The detection scale hint is for SCRFD detection ONLY.
 * `inferEmbedding` always receives the full-resolution frame — see T4.5/T4.7.
 *
 * Tier thresholds (per design D7):
 *   fast   → prewarmMs < 150ms
 *   medium → 150ms ≤ prewarmMs ≤ 300ms  (also the safe default when prewarmMs is undefined)
 *   slow   → prewarmMs > 300ms
 */

/** Performance tier based on measured SCRFD pre-warm inference time. */
export type DeviceTier = 'fast' | 'medium' | 'slow';

/**
 * Classify device performance tier from the pre-warm SCRFD inference time.
 *
 * Returns 'medium' (cautious default) when `prewarmMs` is undefined — this
 * avoids applying aggressive downscaling on devices that haven't completed
 * the pre-warm measurement yet.
 */
export function classifyTier(prewarmMs: number | undefined): DeviceTier {
  if (prewarmMs === undefined) return 'medium';
  if (prewarmMs < 150) return 'fast';
  if (prewarmMs <= 300) return 'medium';
  return 'slow';
}

/**
 * Map a performance tier to the SCRFD detection downscale factor.
 *
 * | Tier   | Scale | Rationale                                           |
 * |--------|-------|-----------------------------------------------------|
 * | fast   | 1.0   | No downscale — full quality, device handles load    |
 * | medium | 0.75  | Moderate savings, minimal accuracy impact           |
 * | slow   | 0.5   | Maximum savings — SCRFD runs on half-resolution     |
 *
 * NOTE: This scale factor MUST NOT be applied to inferEmbedding — MobileFaceNet
 * must always receive a face crop from the full-resolution frame so that enrolled
 * embedding vectors remain valid across device tiers.
 */
export function detectionScaleForTier(tier: DeviceTier): 1.0 | 0.75 | 0.5 {
  if (tier === 'fast') return 1.0;
  if (tier === 'medium') return 0.75;
  return 0.5;
}
